import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { api } from '../hooks/useApi';
import UserAvatar from '../components/UserAvatar';
import {
  DndContext, closestCenter, PointerSensor, TouchSensor,
  useSensor, useSensors
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, useSortable, verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import HomePage      from './HomePage';
import CalendarPage  from './CalendarPage';
import ChatPage      from './ChatPage';
import ShoppingPage  from './ShoppingPage';
import TodoPage      from './TodoPage';
import SettingsPage  from './SettingsPage';
import EmailPage     from './EmailPage';
import MapPage       from './MapPage';
import ContactsPage  from './ContactsPage';
import MealPage      from './MealPage';
import DrivePage     from './DrivePage';
import HomeworkPage  from './HomeworkPage';
import MedicationPage from './MedicationPage';
import BudgetPage    from './BudgetPage';
import AdminPage     from './AdminPage';
import PhotoPage     from './PhotoPage';
import SupportPage   from './SupportPage';

const FamoraLogo = ({ size = 36 }) => (
  <svg width={size} height={size} viewBox="0 0 200 200">
    <defs>
      <linearGradient id="lbas" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#1A1040"/>
        <stop offset="100%" stopColor="#2D1B6B"/>
      </linearGradient>
    </defs>
    <rect width="200" height="200" rx="44" fill="url(#lbas)"/>
    <circle cx="72" cy="62" r="18" fill="none" stroke="#7B9FE8" strokeWidth="6"/>
    <path d="M44 118 Q44 92 72 92 Q100 92 100 118" fill="none" stroke="#7B9FE8" strokeWidth="6" strokeLinecap="round"/>
    <circle cx="138" cy="54" r="16" fill="none" stroke="#F0B45A" strokeWidth="6"/>
    <path d="M113 110 Q113 86 138 86 Q163 86 163 110" fill="none" stroke="#F0B45A" strokeWidth="6" strokeLinecap="round"/>
    <circle cx="105" cy="70" r="14" fill="none" stroke="#9B8FE4" strokeWidth="6"/>
    <path d="M83 118 Q83 100 105 100 Q127 100 127 118" fill="none" stroke="#9B8FE4" strokeWidth="6" strokeLinecap="round"/>
    <path d="M105 158 L88 141 Q80 132 88 123 Q97 114 105 123 Q113 114 122 123 Q130 132 122 141 Z" fill="#F0B45A"/>
    <path d="M48 120 Q45 148 105 162 Q165 148 162 120" fill="none" stroke="#9B8FE4" strokeWidth="3" strokeLinecap="round" opacity="0.6"/>
  </svg>
);

// ─── Alle mulige nav-elementer ────────────────────────────────
const ALL_NAV = [
  { id:'home',        path:'/',            icon:'⊞',  label:'Dashboard', required:true },
  { id:'kalender',    path:'/kalender',    icon:'📅', label:'Kalender'   },
  { id:'chat',        path:'/chat',        icon:'💬', label:'Chat'       },
  { id:'epost',       path:'/epost',       icon:'📧', label:'E-post'     },
  { id:'handleliste', path:'/handleliste', icon:'🛒', label:'Handle'     },
  { id:'todo',        path:'/todo',        icon:'✅', label:'Todo'       },
  { id:'mat',         path:'/mat',         icon:'🍽', label:'Matplan'    },
  { id:'kjoering',    path:'/kjoering',    icon:'🚗', label:'Kjøring'    },
  { id:'lekser',      path:'/lekser',      icon:'📚', label:'Lekser'     },
  { id:'medisin',     path:'/medisin',     icon:'💊', label:'Medisin'    },
  { id:'budsjett',    path:'/budsjett',    icon:'💰', label:'Budsjett'   },
  { id:'kontakter',   path:'/kontakter',   icon:'📞', label:'Kontakter'  },
  { id:'kart',        path:'/kart',        icon:'🗺', label:'Kart'       },
  { id:'bilder',      path:'/bilder',      icon:'📸', label:'Bilder'     },
];

// Fast plasserte bunnlenker — ikke i DnD-listen
const FIXED_NAV = [];

const DEFAULT_NAV = ALL_NAV.map(n => ({ id: n.id, visible: true }));

// ─── Bunnemeny-ikon-komponenter ───────────────────────────────
const IcoHome     = ({c='currentColor'}) => <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9,22 9,12 15,12 15,22"/></svg>;
const IcoCal      = ({c='currentColor'}) => <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const IcoChat     = ({c='currentColor'}) => <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>;
const IcoCart     = ({c='currentColor'}) => <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 001.98 1.61h9.72a2 2 0 001.98-1.61L23 6H6"/></svg>;
const IcoGrid     = ({c='currentColor'}) => <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>;
const IcoBell     = ({c='currentColor'}) => <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>;
const IcoTodo     = ({c='currentColor'}) => <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9,11 12,14 22,4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>;
const IcoMail     = ({c='currentColor'}) => <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>;
const IcoMap      = ({c='currentColor'}) => <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="1,6 1,22 8,18 16,22 23,18 23,2 16,6 8,2"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>;
const IcoPill     = ({c='currentColor'}) => <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.5 20.5a7 7 0 010-9.9L20 1.6a5 5 0 017 7l-9.4 9.4a7 7 0 01-7 2.5z"/><line x1="8.5" y1="8.5" x2="15.5" y2="15.5"/></svg>;
const IcoBook     = ({c='currentColor'}) => <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/></svg>;
const IcoFood     = ({c='currentColor'}) => <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 002-2V2M7 2v20M21 15V2a5 5 0 00-5 5v6c0 1.1.9 2 2 2h3zm0 0v7"/></svg>;
const IcoCar      = ({c='currentColor'}) => <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" rx="2"/><polygon points="16,8 20,8 23,11 23,16 16,16"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>;
const IcoDollar   = ({c='currentColor'}) => <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>;
const IcoPhoto    = ({c='currentColor'}) => <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21,15 16,10 5,21"/></svg>;
const IcoContacts = ({c='currentColor'}) => <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>;

// ─── Alle mulige bunnemeny-elementer (utenom Hjem og Mer) ─────
const NAV_META = {
  kalender:    { label:'Kalender', path:'/kalender',    Ico: IcoCal      },
  chat:        { label:'Chat',     path:'/chat',        Ico: IcoChat     },
  handleliste: { label:'Handle',   path:'/handleliste', Ico: IcoCart     },
  todo:        { label:'Todo',     path:'/todo',        Ico: IcoTodo     },
  epost:       { label:'E-post',   path:'/epost',       Ico: IcoMail     },
  kart:        { label:'Kart',     path:'/kart',        Ico: IcoMap      },
  medisin:     { label:'Medisin',  path:'/medisin',     Ico: IcoPill     },
  lekser:      { label:'Lekser',   path:'/lekser',      Ico: IcoBook     },
  mat:         { label:'Mat',      path:'/mat',         Ico: IcoFood     },
  kjoering:    { label:'Kjøring',  path:'/kjoering',    Ico: IcoCar      },
  budsjett:    { label:'Budsjett', path:'/budsjett',    Ico: IcoDollar   },
  bilder:      { label:'Bilder',   path:'/bilder',      Ico: IcoPhoto    },
  kontakter:   { label:'Kontakter',path:'/kontakter',   Ico: IcoContacts },
};

const DEFAULT_PINNED = ['kalender','chat','handleliste'];
function getBottomPinned() {
  try { return JSON.parse(localStorage.getItem('bottomPinned') || JSON.stringify(DEFAULT_PINNED)); }
  catch { return DEFAULT_PINNED; }
}

function timeAgo(iso) {
  if (!iso) return '';
  const m = Math.floor((Date.now() - new Date(iso)) / 60000);
  if (m < 1)  return 'Akkurat nå';
  if (m < 60) return `${m}m siden`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}t siden`;
  return `${Math.floor(h / 24)}d siden`;
}

// ─── Mobil-hook ───────────────────────────────────────────────
// Regel: desktop-layout KUN i vanlig nettleser på desktop.
// Alt annet (mobil, nettbrett, PWA, TWA, BlueStacks) → app-layout.
function detectMobile() {
  const ua = navigator.userAgent.toLowerCase();
  const isStandalone  = window.matchMedia('(display-mode: standalone)').matches;
  const isMobileUA    = ua.includes('android') || ua.includes('iphone') ||
                        ua.includes('ipad')    || ua.includes('mobile');
  // Desktop-nettleser = ikke installert som app OG ikke mobil UA
  const isDesktopBrowser = !isStandalone && !isMobileUA;
  return !isDesktopBrowser;
}

function useIsMobile() {
  const [mobile, setMobile] = useState(detectMobile);
  useEffect(() => {
    const h = () => setMobile(detectMobile());
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return mobile;
}

// ─── Sortable nav-item ────────────────────────────────────────
function SortableNavItem({ item, isActive, editMode, visible, onNavigate, onToggle, badge }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id, disabled: !editMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  if (editMode) {
    return (
      <div ref={setNodeRef} style={{...style, ...sn.editRow}}>
        <span {...attributes} {...listeners} style={sn.handle}>⠿</span>
        <span style={sn.editIcon}>{item.icon}</span>
        <span style={sn.editLabel}>{item.label}</span>
        {item.required ? (
          <span style={sn.reqBadge}>Påkrevd</span>
        ) : (
          <button style={{...sn.visBtn, ...(visible ? sn.visBtnOn : sn.visBtnOff)}}
            onClick={() => onToggle(item.id)}>
            {visible ? '● Vis' : '○ Skjul'}
          </button>
        )}
      </div>
    );
  }

  if (!visible) return null;

  return (
    <div ref={setNodeRef} style={style}>
      <button
        style={{...sn.navBtn, ...(isActive ? sn.navBtnActive : {})}}
        onClick={() => onNavigate(item.path)}>
        <span style={sn.navIcon}>{item.icon}</span>
        <span style={sn.navLabel}>{item.label}</span>
        {badge > 0 && (
          <span style={{
            marginLeft: 'auto',
            minWidth: 18, height: 18, borderRadius: 9,
            background: '#F7605C', color: '#fff',
            fontSize: 10, fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 5px', lineHeight: 1, flexShrink: 0,
          }}>
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </button>
    </div>
  );
}

// ─── Mer-side (mobil) ────────────────────────────────────────
function MerPage() {
  const { user, family, logout } = useAuth();
  const { dark, toggle } = useTheme();
  const navigate = useNavigate();
  const [openTickets, setOpenTickets] = useState(0);
  const [bottomPinnedLocal, setBottomPinnedLocal] = useState(getBottomPinned);

  useEffect(() => {
    if (!user?.superadmin) return;
    api.get('/api/admin/support/open-count').then(r => setOpenTickets(r?.count || 0)).catch(() => {});
  }, [user?.superadmin]);

  const toggleBottomPin = (id) => {
    setBottomPinnedLocal(prev => {
      const next = prev.includes(id)
        ? prev.filter(x => x !== id)           // fjern
        : prev.length < 3 ? [...prev, id] : prev; // legg til (maks 3)
      localStorage.setItem('bottomPinned', JSON.stringify(next));
      window.dispatchEvent(new CustomEvent('bottomNavChange'));
      return next;
    });
  };

  const ALL_MER_ITEMS = [
    { label:'Familiemedlemmer', emoji:'👥', bg:'#FFE4E4', path:'/innstillinger', required:true },
    { label:'Gjøreliste',       emoji:'✅', bg:'#FFE4E4', path:'/todo'          },
    { label:'Matplanlegger',    emoji:'🍽', bg:'#FFF3CD', path:'/mat'           },
    { label:'Kjøreplan',        emoji:'🚗', bg:'#D1FAE5', path:'/kjoering'      },
    { label:'Lekser',           emoji:'📚', bg:'#EDE9FE', path:'/lekser'        },
    { label:'E-post',           emoji:'📧', bg:'#DBEAFE', path:'/epost'         },
    { label:'Medisinpåminnelser', emoji:'💊', bg:'#FFE4E4', path:'/medisin'     },
    { label:'Familiebudsjett',    emoji:'💰', bg:'#D1FAE5', path:'/budsjett'    },
    { label:'Fotoalbum',   emoji:'🖼',  bg:'#FED7AA', path:'/bilder'            },
    { label:'Familiekart', emoji:'📍',  bg:'#CCFBF1', path:'/kart'             },
    { label:'Kontakter',   emoji:'📞',  bg:'#F3F4F6', path:'/kontakter'         },
    { label:'Innstillinger',emoji:'⚙️', bg:'#F3F4F6', path:'/innstillinger'     },
    { label:'Support',     emoji:'🎫',  bg:'#EDE9FE', path:'/support'           },
    ...(user?.superadmin ? [{ label:'Systemadmin', emoji:'🛡️', bg:'#EDE9FE', path:'/admin', badge: openTickets }] : []),
  ];

  const [hiddenMerItems, setHiddenMerItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem('merHiddenItems') || '[]'); } catch { return []; }
  });
  const [merEditMode, setMerEditMode] = useState(false);

  const toggleMerItem = (label) => {
    setHiddenMerItems(prev => {
      const next = prev.includes(label) ? prev.filter(x => x !== label) : [...prev, label];
      localStorage.setItem('merHiddenItems', JSON.stringify(next));
      return next;
    });
  };

  const SECTION_MAP = [
    { title: 'FAMILIE',         keys: ['Familiemedlemmer','Gjøreliste','Matplanlegger','Kjøreplan','Lekser','E-post'] },
    { title: 'HELSE & ØKONOMI', keys: ['Medisinpåminnelser','Familiebudsjett'] },
    { title: 'ANNET',           keys: ['Fotoalbum','Familiekart','Kontakter','Innstillinger','Support','Systemadmin'] },
  ];

  const sections = SECTION_MAP.map(sec => ({
    title: sec.title,
    items: ALL_MER_ITEMS.filter(i => sec.keys.includes(i.label) && (merEditMode || !hiddenMerItems.includes(i.label))),
  })).filter(sec => sec.items.length > 0);

  const merTxt    = dark ? '#E8E8F8' : '#1A1A35';
  const merSub    = dark ? '#9090B8' : '#888';
  const merBorder = dark ? 'rgba(255,255,255,.07)' : 'rgba(124,111,247,0.12)';

  return (
    <div style={{ background: dark?'#0A0818':'#EDE8FF', minHeight:'100%', fontFamily:"'Plus Jakarta Sans',sans-serif", paddingBottom:32 }}>

      {/* Gradient Hero */}
      <div style={{
        background: dark
          ? 'linear-gradient(145deg, #2D1B69 0%, #1A1245 55%, #0D0B1E 100%)'
          : 'linear-gradient(145deg, #7C6FF7 0%, #9358E8 45%, #5E4AE3 100%)',
        padding:'52px 20px 24px', position:'relative', overflow:'hidden',
      }}>
        <div style={{ position:'absolute', top:-40, right:-40, width:160, height:160, borderRadius:'50%', background:'rgba(255,255,255,0.06)', pointerEvents:'none' }}/>
        <div style={{ position:'absolute', bottom:-60, left:-20, width:140, height:140, borderRadius:'50%', background:'rgba(255,255,255,0.04)', pointerEvents:'none' }}/>

        {/* Tittel-rad med Tilpass-knapp */}
        <div style={{ position:'relative', zIndex:1, display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
          <div>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.65)', fontWeight:500, marginBottom:2 }}>Famora</div>
            <div style={{ fontSize:24, fontWeight:800, color:'#fff' }}>Mer</div>
          </div>
          <button onClick={() => merEditMode ? setMerEditMode(false) : setMerEditMode(true)}
            style={{ background:'rgba(255,255,255,0.18)', backdropFilter:'blur(8px)', border:'1px solid rgba(255,255,255,0.25)', color:'#fff', borderRadius:12, padding:'8px 16px', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit' }}>
            {merEditMode ? 'Ferdig' : 'Tilpass'}
          </button>
        </div>

        {/* Profil-kort inne i hero */}
        <div onClick={() => navigate('/innstillinger')} style={{ position:'relative', zIndex:1, background:'rgba(255,255,255,0.15)', backdropFilter:'blur(12px)', border:'1px solid rgba(255,255,255,0.25)', borderRadius:16, padding:'14px 16px', display:'flex', alignItems:'center', gap:14, cursor:'pointer' }}>
          <div style={{ width:48, height:48, borderRadius:'50%', background:user?.color||'rgba(255,255,255,0.3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, fontWeight:800, color:'#fff', flexShrink:0, border:'2px solid rgba(255,255,255,0.4)' }}>
            {user?.avatar || (user?.name||'?')[0].toUpperCase()}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:15, fontWeight:800, color:'#fff', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user?.name}</div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,0.72)', marginTop:2 }}>{family?.name || user?.email}</div>
          </div>
          <span style={{ color:'rgba(255,255,255,0.6)', fontSize:20 }}>›</span>
        </div>
      </div>

      {/* Bunnemeny-velger */}
      <div style={{ margin:'12px 14px 12px' }}>
        <div style={{ fontSize:11, fontWeight:700, color:merSub, letterSpacing:1, textTransform:'uppercase', marginBottom:8, paddingLeft:2 }}>
          Bunnemeny — velg inntil 3
        </div>
        <div className="glass-card" style={{ padding:'14px 12px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
            {Object.entries(NAV_META).map(([id, meta]) => {
              const isPinned = bottomPinnedLocal.includes(id);
              const isFull   = bottomPinnedLocal.length >= 3 && !isPinned;
              const Ico = meta.Ico;
              return (
                <button key={id}
                  onClick={() => !isFull && toggleBottomPin(id)}
                  style={{
                    display:'flex', flexDirection:'column', alignItems:'center', gap:4,
                    padding:'10px 4px', borderRadius:12, border:'none', cursor: isFull?'default':'pointer',
                    background: isPinned ? 'rgba(124,111,247,0.15)' : dark?'rgba(255,255,255,0.05)':'rgba(124,111,247,0.07)',
                    opacity: isFull ? 0.35 : 1,
                    transition:'background .15s',
                    fontFamily:'inherit',
                  }}>
                  <Ico c={isPinned ? '#7C6FF7' : merSub}/>
                  <span style={{ fontSize:9, fontWeight:isPinned?700:500, color: isPinned?'#7C6FF7':merSub, textAlign:'center', lineHeight:1.2 }}>
                    {meta.label}
                  </span>
                  {isPinned && <div style={{ width:4, height:4, borderRadius:'50%', background:'#7C6FF7' }}/>}
                </button>
              );
            })}
          </div>
          <div style={{ textAlign:'center', fontSize:11, color:merSub, marginTop:10 }}>
            {bottomPinnedLocal.length}/3 valgt — Hjem og Mer er alltid faste
          </div>
        </div>
      </div>

      {/* Seksjoner */}
      {sections.map(sec => (
        <div key={sec.title} style={{ margin:'0 14px 12px' }}>
          <div style={{ fontSize:11, fontWeight:700, color:merSub, letterSpacing:1, textTransform:'uppercase', marginBottom:8, paddingLeft:2 }}>{sec.title}</div>
          <div className="glass-card" style={{ overflow:'hidden', padding:0 }}>
            {sec.items.map((item, i) => {
              const isHidden = hiddenMerItems.includes(item.label);
              return (
                <div key={item.label} style={{ display:'flex', alignItems:'center', borderTop: i>0?`1px solid ${merBorder}`:'none' }}>
                  <button
                    style={{ flex:1, display:'flex', alignItems:'center', gap:14, padding:'13px 16px', background:'none', border:'none', cursor: merEditMode?'default':'pointer', fontFamily:'inherit', textAlign:'left', opacity: merEditMode&&isHidden?0.4:1 }}
                    onClick={() => !merEditMode && navigate(item.path)}>
                    <div style={{ width:38, height:38, borderRadius:12, background: dark?'rgba(255,255,255,0.08)':item.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>
                      {item.emoji}
                    </div>
                    <span style={{ flex:1, fontSize:15, fontWeight:600, color:merTxt }}>{item.label}</span>
                    {!merEditMode && item.badge > 0 && (
                      <span style={{ minWidth:20, height:20, borderRadius:10, background:'#7C6FF7', color:'#fff', fontSize:11, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 6px' }}>
                        {item.badge > 9 ? '9+' : item.badge}
                      </span>
                    )}
                    {!merEditMode && <span style={{ color:merSub, fontSize:18 }}>›</span>}
                  </button>
                  {merEditMode && !item.required && (
                    <button onClick={() => toggleMerItem(item.label)}
                      style={{ padding:'13px 16px', background:'none', border:'none', cursor:'pointer', flexShrink:0 }}>
                      <div style={{ width:46, height:26, borderRadius:13, background: isHidden?dark?'rgba(255,255,255,0.12)':'rgba(0,0,0,0.12)':'#7C6FF7', position:'relative', transition:'background .2s' }}>
                        <div style={{ position:'absolute', top:3, left: isHidden?3:23, width:20, height:20, borderRadius:'50%', background:'#fff', boxShadow:'0 1px 4px rgba(0,0,0,.2)', transition:'left .2s' }}/>
                      </div>
                    </button>
                  )}
                  {merEditMode && item.required && (
                    <span style={{ padding:'13px 16px', fontSize:11, fontWeight:700, color:merSub, flexShrink:0 }}>Påkrevd</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Tema + Logg ut */}
      <div style={{ margin:'0 14px 16px' }}>
        <div className="glass-card" style={{ overflow:'hidden', padding:0 }}>
          <button onClick={toggle}
            style={{ width:'100%', display:'flex', alignItems:'center', gap:14, padding:'13px 16px', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', textAlign:'left' }}>
            <div style={{ width:38, height:38, borderRadius:12, background: dark?'rgba(255,255,255,0.08)':'rgba(124,111,247,0.12)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>
              {dark ? '☀️' : '🌙'}
            </div>
            <span style={{ flex:1, fontSize:15, fontWeight:600, color:merTxt }}>{dark ? 'Lyst tema' : 'Mørkt tema'}</span>
            <span style={{ color:merSub, fontSize:18 }}>›</span>
          </button>
          <button onClick={() => logout()}
            style={{ width:'100%', display:'flex', alignItems:'center', gap:14, padding:'13px 16px', background:'none', border:'none', borderTop:`1px solid ${merBorder}`, cursor:'pointer', fontFamily:'inherit', textAlign:'left' }}>
            <div style={{ width:38, height:38, borderRadius:12, background:'rgba(229,83,75,0.12)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>
              🚪
            </div>
            <span style={{ flex:1, fontSize:15, fontWeight:600, color:'#E5534B' }}>Logg ut</span>
            <span style={{ color:'#E5534B', fontSize:18 }}>›</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Hoved-komponent ─────────────────────────────────────────
export default function AppShell() {
  const { user, family, logout } = useAuth();
  const { dark, toggle } = useTheme();
  const navigate   = useNavigate();
  const location   = useLocation();
  const isMobile   = useIsMobile();

  // Debug: logg UA og skjerminfo — fjernes etter testing
  useEffect(() => {
    const ua = navigator.userAgent;
    const w  = window.innerWidth;
    const standalone = window.matchMedia('(display-mode: standalone)').matches;
    console.log('[Famora] UserAgent:', ua);
    console.log('[Famora] innerWidth:', w, '| isMobile:', detectMobile(), '| standalone:', standalone);
  }, []);

  const [open, setOpen]               = useState(false);
  const [editMode, setEditMode]       = useState(false);
  const [navLayout, setNavLayout]     = useState(null);
  const [saving, setSaving]           = useState(false);
  const [origLayout, setOrigLayout]   = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showMore, setShowMore]       = useState(false);
  const mainRef = useRef(null);

  // Global back-swipe fra venstre kant (mobil) → navigate(-1)
  useEffect(() => {
    if (!isMobile) return;
    const el = mainRef.current;
    if (!el) return;
    let startX = 0, startY = 0;
    const onStart = e => {
      if (e.touches[0].clientX > 25) { startX = 0; return; }
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };
    const onEnd = e => {
      if (!startX) return;
      const dx = e.changedTouches[0].clientX - startX;
      const dy = Math.abs(e.changedTouches[0].clientY - startY);
      startX = 0;
      // /epost håndterer sin egen tilbake-navigasjon (detail → liste)
      if (dx > 90 && dy < 100 && !location.pathname.startsWith('/epost')) navigate(-1);
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchend',   onEnd,   { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchend',   onEnd);
    };
  }, [isMobile, navigate]);

  // ── Varsler ──
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen]         = useState(false);

  // Badge-telling per nav-modul basert på uleste varsler
  const navBadges = React.useMemo(() => {
    const unread = notifications.filter(n => !n.read);
    const counts = {};
    unread.forEach(n => {
      const typeMap = { message:'chat', event:'kalender', todo:'todo', reminder:'kalender' };
      const key = typeMap[n.type];
      if (key) counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [notifications]);

  // ── Åpne support-saker (badge for superadmin) ──
  const [openTickets, setOpenTickets] = useState(0);
  useEffect(() => {
    if (!user?.superadmin) return;
    const load = () => api.get('/api/admin/support/open-count')
      .then(r => setOpenTickets(r?.count || 0)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [user?.superadmin]);

  const loadNotifs = useCallback(async () => {
    try { setNotifications(await api.get('/api/notifications') || []); } catch {}
  }, []);

  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 60000);
    return () => clearInterval(t);
  }, [loadNotifs]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAllRead = useCallback(async () => {
    await api.put('/api/notifications/read-all', {}).catch(() => {});
    setNotifications(prev => prev.map(n => ({ ...n, read: 1 })));
  }, []);

  const handleNotifClick = useCallback(async (notif) => {
    if (!notif.read) {
      await api.put(`/api/notifications/${notif.id}/read`, {}).catch(() => {});
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: 1 } : n));
    }
    setNotifOpen(false);
    if (notif.link) navigate(notif.link);
  }, [navigate]);

  const clearRead = useCallback(async () => {
    await api.delete('/api/notifications/read').catch(() => {});
    setNotifications(prev => prev.filter(n => !n.read));
  }, []);

  // Last nav-layout
  useEffect(() => {
    api.get('/api/auth/nav-layout').then(data => {
      if (data && Array.isArray(data)) {
        // Migrer: legg til nye nav-items som mangler
        const migrated = [...data];
        for (const item of ALL_NAV) {
          if (!migrated.find(m => m.id === item.id)) {
            migrated.push({ id: item.id, visible: true });
          }
        }
        setNavLayout(migrated);
        setOrigLayout(migrated);
      } else {
        setNavLayout(DEFAULT_NAV);
        setOrigLayout(DEFAULT_NAV);
      }
    }).catch(() => {
      setNavLayout(DEFAULT_NAV);
      setOrigLayout(DEFAULT_NAV);
    });
  }, []);

  // ── Bunnemeny-tilpasning ──
  const [bottomPinned, setBottomPinned] = useState(getBottomPinned);
  useEffect(() => {
    const h = () => setBottomPinned(getBottomPinned());
    window.addEventListener('bottomNavChange', h);
    return () => window.removeEventListener('bottomNavChange', h);
  }, []);

  // Lukk profil-meny ved klikk utenfor
  useEffect(() => {
    const close = (e) => {
      if (!e.target.closest('#profile-menu-trigger'))
        setShowProfileMenu(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  // Lukk på navigasjon (mobil)
  useEffect(() => { if (isMobile) setOpen(false); }, [location.pathname, isMobile]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor,   { activationConstraint: { delay: 150, tolerance: 6 } })
  );

  const onDragEnd = useCallback(({ active, over }) => {
    if (!over || active.id === over.id) return;
    setNavLayout(l => arrayMove(l, l.findIndex(x => x.id === active.id), l.findIndex(x => x.id === over.id)));
  }, []);

  const toggleItem = useCallback(id => {
    setNavLayout(l => l.map(x => x.id === id ? { ...x, visible: !x.visible } : x));
  }, []);

  const save = async () => {
    setSaving(true);
    await api.put('/api/auth/nav-layout', navLayout).catch(() => {});
    setOrigLayout(navLayout);
    setSaving(false);
    setEditMode(false);
  };

  const cancel = () => {
    setNavLayout(origLayout);
    setEditMode(false);
  };

  const currentTitle = (() => {
    if (location.pathname === '/admin') return 'Systemadmin';
    const found = [...ALL_NAV, ...FIXED_NAV].find(n => n.path === location.pathname);
    return found?.label || 'Dashboard';
  })();

  if (!navLayout) return null;

  // Bygg den faktiske nav-listen i layoutrekkefølge
  const orderedNav = navLayout
    .map(({ id, visible }) => {
      const meta = ALL_NAV.find(n => n.id === id);
      return meta ? { ...meta, visible } : null;
    })
    .filter(Boolean);

  // "Mer"-badge: sum av varsler i moduler som IKKE er i bunnemeny
  const bottomTabIds = new Set(['home', ...bottomPinned]);
  const moreBadge = Object.entries(navBadges)
    .filter(([k]) => !bottomTabIds.has(k))
    .reduce((s, [, v]) => s + v, 0);

  // ── MOBIL: flex-kolonne med in-flow elementer — pålitelig på alle enheter ──
  if (isMobile) return (
    <div style={{ display:'flex', flexDirection:'column', height:'100dvh', overflow:'hidden', background: dark?'#0D0B1E':'#F7F5FF', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>

      {/* Famora-topplinje — in-flow, alltid riktig plassert */}
      <div style={{
        height:'calc(env(safe-area-inset-top, 0px) + 30px)',
        paddingTop:'env(safe-area-inset-top, 0px)',
        background: dark ? '#0D0B1E' : '#ffffff',
        borderBottom:`1px solid ${dark?'rgba(255,255,255,.07)':'rgba(0,0,0,.06)'}`,
        display:'flex', alignItems:'flex-end', justifyContent:'center',
        paddingBottom:6, flexShrink:0,
      }}>
        <span style={{ fontSize:13, fontWeight:800, color: dark?'#E8E8F8':'#1A1A35', letterSpacing:.5 }}>Famora</span>
      </div>

      {/* Mobil sidebar — position:fixed for slide-in */}
      <nav style={{ ...sn.sidebar, background: dark?'#080818':'#16162A', position:'fixed', top:0, bottom:0, left:0, transform: open?'translateX(0)':'translateX(-100%)', transition:'transform .22s ease', zIndex:300, boxShadow: open?'4px 0 24px rgba(0,0,0,.25)':'none' }}>
        <div style={sn.sideTop}>
          <div style={{ display:'flex', justifyContent:'center', marginBottom:6 }}><FamoraLogo size={36}/></div>
          <div style={sn.famName}>Famora</div>
          <div style={sn.famTagline}>En app for hele familien</div>
        </div>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={navLayout.map(x=>x.id)} strategy={verticalListSortingStrategy}>
            <div style={sn.navList}>
              {orderedNav.map(item=>(
                <SortableNavItem key={item.id} item={item} isActive={location.pathname===item.path}
                  editMode={editMode} visible={item.visible}
                  onNavigate={p=>{navigate(p);setOpen(false);}} onToggle={toggleItem} badge={navBadges[item.id]||0}/>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </nav>

      {open && <div style={sn.overlay} onClick={()=>setOpen(false)}/>}

      {/* Innholdsområde — fyller rommet mellom topplinje og bunnemeny */}
      <main ref={mainRef} style={{ flex:1, overflowY:'auto', overflowX:'hidden', minHeight:0, background: dark?'#0D0B1E':'#F7F5FF' }}>
        <div key={location.pathname} className="page-enter">
          <Routes>
            <Route path="/"               element={<HomePage />} />
            <Route path="/kalender"       element={<CalendarPage />} />
            <Route path="/chat"           element={<ChatPage />} />
            <Route path="/epost"          element={<EmailPage />} />
            <Route path="/handleliste"    element={<ShoppingPage />} />
            <Route path="/todo"           element={<TodoPage />} />
            <Route path="/mat"            element={<MealPage />} />
            <Route path="/kjoering"       element={<DrivePage />} />
            <Route path="/lekser"         element={<HomeworkPage />} />
            <Route path="/medisin"        element={<MedicationPage />} />
            <Route path="/budsjett"       element={<BudgetPage />} />
            <Route path="/kontakter"      element={<ContactsPage />} />
            <Route path="/kart"           element={<MapPage />} />
            <Route path="/bilder"         element={<PhotoPage />} />
            <Route path="/support"        element={<SupportPage />} />
            <Route path="/innstillinger"  element={<SettingsPage />} />
            <Route path="/admin"          element={user?.superadmin ? <AdminPage /> : <Navigate to="/" replace />} />
            <Route path="/mer"            element={<MerPage />} />
          </Routes>
        </div>
      </main>

      {/* Bunnemeny — flytende pill */}
      <nav style={{
        flexShrink: 0,
        background: dark ? '#0A0818' : '#EDE8FF',
        padding: '4px 14px',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 4px)',
      }}>
        <div style={{
          background: dark ? 'rgba(28,26,52,0.96)' : 'rgba(255,255,255,0.96)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderRadius: 30,
          height: 58,
          display: 'flex',
          alignItems: 'center',
          boxShadow: dark
            ? '0 2px 28px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)'
            : '0 2px 28px rgba(124,111,247,0.20), 0 1px 3px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.95)',
          border: dark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(255,255,255,0.85)',
          overflow: 'hidden',
        }}>
          {/* Hjem — alltid fast */}
          {[{ id:'home', path:'/', label:'Hjem', Ico: IcoHome },
            ...bottomPinned.map(id => ({ id, ...NAV_META[id] })).filter(t => t.path)
          ].map(tab => {
            const isActive = location.pathname === tab.path || (tab.path !== '/' && location.pathname.startsWith(tab.path));
            const badge    = navBadges[tab.id] || 0;
            const color    = isActive ? '#7C6FF7' : (dark ? '#8885AA' : '#9B99B8');
            return (
              <button key={tab.id}
                style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:3, background:'none', border:'none', cursor:'pointer', padding:'0 4px', position:'relative', height:'100%' }}
                onClick={() => navigate(tab.path)}>
                {isActive && <span style={{ position:'absolute', inset:'6px 8px', background:'linear-gradient(135deg,rgba(124,111,247,.18),rgba(229,83,75,.08))', borderRadius:22, pointerEvents:'none' }}/>}
                <span style={{ position:'relative', display:'flex' }}>
                  <tab.Ico c={color}/>
                  {badge > 0 && (
                    <span style={{ position:'absolute', top:-3, right:-5, minWidth:14, height:14, borderRadius:7, background:'#F7605C', color:'#fff', fontSize:9, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 3px' }}>
                      {badge > 9 ? '9+' : badge}
                    </span>
                  )}
                </span>
                <span style={{ fontSize:10, fontWeight:isActive?700:500, color }}>{tab.label}</span>
              </button>
            );
          })}
          {/* Mer-knapp */}
          {(() => {
            const merActive = location.pathname === '/mer';
            const merColor  = merActive ? '#7C6FF7' : (dark ? '#8885AA' : '#9B99B8');
            return (
              <button
                style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:3, background:'none', border:'none', cursor:'pointer', padding:'0 4px', position:'relative', height:'100%' }}
                onClick={() => navigate('/mer')}>
                {merActive && <span style={{ position:'absolute', inset:'6px 8px', background:'linear-gradient(135deg,rgba(124,111,247,.18),rgba(229,83,75,.08))', borderRadius:22, pointerEvents:'none' }}/>}
                <span style={{ position:'relative', display:'flex' }}>
                  <IcoGrid c={merColor}/>
                  {moreBadge > 0 && (
                    <span style={{ position:'absolute', top:-3, right:-5, minWidth:14, height:14, borderRadius:7, background:'#F7605C', color:'#fff', fontSize:9, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 3px' }}>
                      {moreBadge > 9 ? '9+' : moreBadge}
                    </span>
                  )}
                </span>
                <span style={{ fontSize:10, fontWeight:merActive?700:500, color:merColor }}>Mer</span>
              </button>
            );
          })()}
        </div>
      </nav>
    </div>
  );

  // ── DESKTOP: original layout ──
  return (
    <div style={{...sn.shell, background: dark ? '#0A0818' : '#EDE8FF'}}>

      {/* ── Sidebar (desktop: alltid synlig, mobil: skjult/slide-in via ☰) ── */}
      <nav style={{
        ...sn.sidebar,
        ...(isMobile ? {
          position:'fixed', top:0, bottom:0, left:0,
          transform: open ? 'translateX(0)' : 'translateX(-100%)',
          transition:'transform .22s ease',
          zIndex:300,
          boxShadow: open ? '4px 0 24px rgba(0,0,0,.25)' : 'none',
        } : {}),
      }}>
        {/* Gradient brand-seksjon */}
        <div style={{
          background: 'linear-gradient(160deg, #7C6FF7 0%, #5E4AE3 100%)',
          padding: '22px 16px 18px',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position:'absolute', top:-20, right:-20, width:90, height:90, borderRadius:'50%', background:'rgba(255,255,255,0.1)', pointerEvents:'none' }}/>
          <div style={{ position:'absolute', bottom:-30, left:-10, width:70, height:70, borderRadius:'50%', background:'rgba(255,255,255,0.07)', pointerEvents:'none' }}/>
          <div style={{ display:'flex', alignItems:'center', gap:10, position:'relative', zIndex:1 }}>
            <FamoraLogo size={38} />
            <div>
              <div style={sn.famName}>Famora</div>
              <div style={sn.famTagline}>En app for hele familien</div>
            </div>
          </div>
        </div>

        {/* Nav-elementer med DnD */}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={navLayout.map(x => x.id)} strategy={verticalListSortingStrategy}>
            <div style={sn.navList}>
              {orderedNav.map(item => (
                <SortableNavItem
                  key={item.id}
                  item={item}
                  isActive={location.pathname === item.path}
                  editMode={editMode}
                  visible={item.visible}
                  onNavigate={p => { navigate(p); setOpen(false); }}
                  onToggle={toggleItem}
                  badge={navBadges[item.id] || 0}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {/* Tilpass meny */}
        {!editMode && (
          <button
            onClick={() => setEditMode(true)}
            style={{
              width:'100%', display:'flex', alignItems:'center',
              gap:8, padding:'9px 14px',
              background:'none', border:'none',
              borderTop:'1px solid rgba(255,255,255,0.07)',
              cursor:'pointer', color:'rgba(255,255,255,0.35)',
              fontSize:12, fontWeight:600,
              fontFamily:'inherit', transition:'background .15s',
            }}
            onMouseOver={e => e.currentTarget.style.background='rgba(255,255,255,0.05)'}
            onMouseOut={e  => e.currentTarget.style.background='none'}
          >
            ✏️ Tilpass meny
          </button>
        )}

        {/* Bunn-seksjon */}
        <div style={sn.sideBot}>
          {editMode ? (
            <>
              <div style={sn.editActions}>
                <button style={sn.saveBtn} onClick={save} disabled={saving}>
                  {saving ? '…' : '💾 Lagre'}
                </button>
                <button style={sn.cancelBtn} onClick={cancel}>Avbryt</button>
              </div>
              <div style={sn.editHint}>⠿ Dra for å flytte · Klikk for å vise/skjule</div>
              <button style={{...sn.editNavBtn, marginTop:4}} onClick={() => setEditMode(false)} title="Lukk">✕</button>
            </>
          ) : (
            <div style={{ position:'relative' }}>

              {/* Hurtigmeny */}
              {showProfileMenu && (
                <div style={{
                  position:'absolute', bottom:'100%', left:0, right:0,
                  marginBottom:6,
                  background:'rgba(18,15,38,0.97)',
                  backdropFilter:'blur(24px)', WebkitBackdropFilter:'blur(24px)',
                  border:'1px solid rgba(124,111,247,0.2)',
                  borderRadius:16, overflow:'hidden',
                  boxShadow:'0 -8px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)',
                  zIndex:100,
                }}>
                  {/* Profilinfo */}
                  <div style={{ padding:'12px 14px 8px', borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
                    <div style={{ fontSize:13, fontWeight:700, color:'#E8E8F8', marginBottom:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user?.name}</div>
                    <div style={{ fontSize:11, color:'#6B6B8A', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user?.email}</div>
                    <div style={{ marginTop:5, fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:20, display:'inline-block',
                      background: user?.role==='admin' ? 'rgba(124,111,247,0.2)' : 'rgba(52,211,153,0.15)',
                      color: user?.role==='admin' ? '#9B8FFF' : '#34D399',
                    }}>
                      {user?.role==='admin' ? '👑 Administrator' : '👤 Medlem'}
                    </div>
                  </div>

                  {/* Menyvalg */}
                  {[
                    { icon: dark ? '☀️' : '🌙', label: dark ? 'Lyst tema' : 'Mørkt tema', keepOpen: true, action: () => { toggle(); } },
                    { icon:'🎫', label:'Support',       action: () => { navigate('/support');                  setShowProfileMenu(false); setOpen(false); } },
                    ...(user?.superadmin ? [{ icon:'🛡️', label:'Systemadmin', action: () => { navigate('/admin'); setShowProfileMenu(false); setOpen(false); }, badge: openTickets > 0 ? openTickets : null }] : []),
                    { icon:'⚙️', label:'Innstillinger', action: () => { navigate('/innstillinger');            setShowProfileMenu(false); setOpen(false); } },
                  ].map((item, i) => (
                    <button key={i} onClick={item.action} style={{ width:'100%', display:'flex', alignItems:'center', gap:10, padding:'9px 14px', background:'none', border:'none', cursor:'pointer', textAlign:'left', position:'relative' }}
                      onMouseOver={e => e.currentTarget.style.background='rgba(255,255,255,0.05)'}
                      onMouseOut={e  => e.currentTarget.style.background='none'}
                    >
                      <span style={{ fontSize:16, flexShrink:0 }}>{item.icon}</span>
                      <span style={{ fontSize:13, fontWeight:600, color:'#E8E8F8', flex:1 }}>{item.label}</span>
                      {item.badge && <span style={{ minWidth:18, height:18, borderRadius:9, background:'#F7605C', color:'#fff', fontSize:10, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 5px' }}>{item.badge > 9 ? '9+' : item.badge}</span>}
                    </button>
                  ))}

                  <div style={{ height:1, background:'rgba(255,255,255,0.08)', margin:'2px 0' }}/>

                  {/* Logg ut */}
                  <button
                    onClick={() => { logout(); setShowProfileMenu(false); }}
                    style={{ width:'100%', display:'flex', alignItems:'center', gap:10, padding:'9px 14px', background:'none', border:'none', cursor:'pointer', textAlign:'left', color:'#F7605C' }}
                    onMouseOver={e => e.currentTarget.style.background='rgba(247,96,92,0.1)'}
                    onMouseOut={e  => e.currentTarget.style.background='none'}
                  >
                    <span style={{ fontSize:16 }}>🚪</span>
                    <span style={{ fontSize:13, fontWeight:600 }}>Logg ut</span>
                  </button>
                </div>
              )}

              {/* Profil-rad */}
              <div id="profile-menu-trigger"
                onClick={e => { e.stopPropagation(); setShowProfileMenu(v => !v); }}
                style={{
                  display:'flex', alignItems:'center', gap:9,
                  padding:'9px 10px', cursor:'pointer',
                  borderTop:'1px solid rgba(255,255,255,0.07)',
                  borderRadius:10,
                  background: showProfileMenu ? 'rgba(255,255,255,0.06)' : 'transparent',
                  transition:'background .15s', userSelect:'none',
                }}
                onMouseOver={e => { if (!showProfileMenu) e.currentTarget.style.background='rgba(255,255,255,0.05)'; }}
                onMouseOut={e  => { if (!showProfileMenu) e.currentTarget.style.background='transparent'; }}
              >
                <UserAvatar photo={user?.photo} emoji={user?.avatar} name={user?.name} color={user?.color} size={32}
                  style={{ border: showProfileMenu ? '2px solid #9B8FFF' : '2px solid rgba(255,255,255,0.1)', flexShrink:0 }}
                />
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'#E8E8F8', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user?.name}</div>
                  <div style={{ fontSize:10, color: user?.role==='admin' ? '#9B8FFF' : '#6B6B8A', fontWeight:500 }}>
                    {user?.role==='admin' ? '👑 Admin' : '👤 Medlem'}
                  </div>
                </div>
                <span style={{ color:'#6B6B8A', fontSize:10, transform: showProfileMenu ? 'rotate(180deg)' : 'rotate(0deg)', transition:'transform .2s', flexShrink:0 }}>▲</span>
              </div>

            </div>
          )}
        </div>
      </nav>}

      {/* Overlay (mobil sidebar) */}
      {isMobile && open && (
        <div style={sn.overlay} onClick={() => setOpen(false)} />
      )}

      {/* Hovedinnhold */}
      <div style={sn.main}>
        <header style={{
          ...sn.topbar,
          background: dark
            ? 'linear-gradient(135deg, #1A0A4E 0%, #2D1B6B 100%)'
            : 'linear-gradient(135deg, #7C6FF7 0%, #9358E8 100%)',
          boxShadow: '0 2px 20px rgba(0,0,0,0.25)',
          display: isMobile ? 'none' : 'flex',
        }}>
          <button style={sn.menuBtn} onClick={() => setOpen(o => !o)}>☰</button>
          <h1 style={{...sn.pageTitle, color:'#fff', textShadow:'0 1px 4px rgba(0,0,0,0.2)'}}>{currentTitle}</h1>
          <div style={sn.topRight}>

            {/* Varselklokke */}
            <div style={{ position:'relative' }}>
              <button style={sn.notifBtn} onClick={() => setNotifOpen(v => !v)} title="Varsler">
                🔔
                {unreadCount > 0 && (
                  <span style={sn.notifBadge}>{unreadCount > 9 ? '9+' : unreadCount}</span>
                )}
              </button>

              {notifOpen && (
                <>
                  <div style={sn.notifOverlay} onClick={() => setNotifOpen(false)} />
                  <div style={sn.notifPanel}>
                    <div style={sn.notifHeader}>
                      <span style={sn.notifTitle}>Varsler</span>
                      <div style={{ display:'flex', gap:6 }}>
                        {unreadCount > 0 && (
                          <button style={sn.notifAction} onClick={markAllRead}>✓ Alle lest</button>
                        )}
                        {notifications.some(n => n.read) && (
                          <button style={{...sn.notifAction, color:'#F7605C'}} onClick={clearRead}>🗑 Slett leste</button>
                        )}
                      </div>
                    </div>
                    <div style={sn.notifList}>
                      {notifications.length === 0 && (
                        <div style={sn.notifEmpty}>Ingen varsler enda</div>
                      )}
                      {notifications.map(n => (
                        <div
                          key={n.id}
                          style={{ ...sn.notifItem, ...(!n.read ? sn.notifItemUnread : {}) }}
                          onClick={() => handleNotifClick(n)}
                        >
                          <span style={sn.notifItemIcon}>
                            {n.type==='message'?'💬':n.type==='event'?'📅':n.type==='todo'?'✅':n.type==='reminder'?'⏰':'🔔'}
                          </span>
                          <div style={sn.notifItemBody}>
                            <div style={sn.notifItemTitle}>{n.title}</div>
                            {n.body && <div style={sn.notifItemText}>{n.body}</div>}
                            <div style={sn.notifItemTime}>{timeAgo(n.created_at)}</div>
                          </div>
                          {!n.read && <div style={sn.notifDot} />}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

          </div>
        </header>

        <main style={{...sn.content, background: dark ? '#0A0818' : '#EDE8FF'}}>
          <div key={location.pathname} className="page-enter">
            <Routes>
              <Route path="/"               element={<HomePage />} />
              <Route path="/kalender"       element={<CalendarPage />} />
              <Route path="/chat"           element={<ChatPage />} />
              <Route path="/epost"          element={<EmailPage />} />
              <Route path="/handleliste"    element={<ShoppingPage />} />
              <Route path="/todo"           element={<TodoPage />} />
              <Route path="/mat"            element={<MealPage />} />
              <Route path="/kjoering"       element={<DrivePage />} />
              <Route path="/lekser"         element={<HomeworkPage />} />
              <Route path="/medisin"        element={<MedicationPage />} />
              <Route path="/budsjett"       element={<BudgetPage />} />
              <Route path="/kontakter"      element={<ContactsPage />} />
              <Route path="/kart"           element={<MapPage />} />
              <Route path="/bilder"         element={<PhotoPage />} />
              <Route path="/support"        element={<SupportPage />} />
              <Route path="/innstillinger"  element={<SettingsPage />} />
              <Route path="/admin"          element={user?.superadmin ? <AdminPage /> : <Navigate to="/" replace />} />
              <Route path="/mer"            element={<MerPage />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────
const sn = {
  shell:        { display:'flex', height:'100vh', overflow:'hidden', fontFamily:"'Plus Jakarta Sans',sans-serif" },
  sidebar:      { width:230, background:'#0F0D22', display:'flex', flexDirection:'column', flexShrink:0, overflowY:'auto' },
  sideTop:      { padding:'0 10px 8px' },
  logo:         { fontSize:28, marginBottom:3 },
  famName:      { fontSize:13, fontWeight:800, color:'rgba(255,255,255,0.9)', letterSpacing:.3 },
  famTagline:   { fontSize:9, fontWeight:500, color:'rgba(255,255,255,0.38)', letterSpacing:.2, marginTop:2 },
  navList:      { display:'flex', flexDirection:'column', gap:2, flex:1, padding:'0 10px' },
  fixedNav:     { display:'flex', flexDirection:'column', gap:1, borderTop:'1px solid rgba(255,255,255,.07)', paddingTop:6, marginTop:4 },
  navBtn:       { display:'flex', alignItems:'center', gap:10, padding:'9px 12px', borderRadius:12, border:'none', background:'transparent', color:'rgba(255,255,255,0.38)', cursor:'pointer', width:'100%', textAlign:'left', transition:'.12s', fontSize:13, fontFamily:'inherit', position:'relative' },
  navBtnActive: { background:'rgba(124,111,247,0.18)', color:'#A78BFA', borderLeft:'3px solid #7C6FF7', paddingLeft:9 },
  navIcon:      { fontSize:15, width:20, textAlign:'center', flexShrink:0 },
  navLabel:     { fontWeight:600, fontFamily:'inherit' },
  sideBot:      { marginTop:8, padding:'10px 10px 12px', borderTop:'1px solid rgba(255,255,255,.07)' },
  userRow:      { display:'flex', alignItems:'center', gap:8, padding:'4px 2px', marginBottom:6 },
  userInfo:     { minWidth:0 },
  userName:     { fontSize:12, fontWeight:700, color:'#E8E8F0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  userRole:     { fontSize:10, color:'#6B6B8A' },
  avatar:       { width:34, height:34, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:14, color:'#fff', flexShrink:0 },
  botBtns:      { display:'flex', gap:6, alignItems:'center' },
  editNavBtn:   { width:32, height:32, borderRadius:8, border:'1px solid rgba(255,255,255,.1)', background:'transparent', color:'#9090B8', cursor:'pointer', fontSize:14, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 },
  logoutBtn:    { flex:1, padding:'6px', borderRadius:8, border:'1px solid rgba(255,255,255,.08)', background:'transparent', color:'#6B6B8A', cursor:'pointer', fontFamily:'inherit', fontSize:11, fontWeight:600 },
  editActions:  { display:'flex', gap:6, marginBottom:8 },
  saveBtn:      { flex:1, padding:'7px', borderRadius:8, border:'none', background:'linear-gradient(135deg,#7C6FF7,#9358E8)', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:12, fontFamily:'inherit' },
  cancelBtn:    { flex:1, padding:'7px', borderRadius:8, border:'1px solid rgba(255,255,255,.1)', background:'transparent', color:'#9090B8', cursor:'pointer', fontSize:12, fontFamily:'inherit' },
  editHint:     { fontSize:10, color:'#5B5B80', textAlign:'center', marginTop:6, lineHeight:1.4 },
  // Edit-modus nav-items
  editRow:      { display:'flex', alignItems:'center', gap:7, padding:'6px 8px', borderRadius:9, background:'rgba(255,255,255,.04)', marginBottom:2 },
  handle:       { fontSize:15, color:'#5B5B80', cursor:'grab', userSelect:'none', letterSpacing:1, flexShrink:0 },
  editIcon:     { fontSize:14, width:18, textAlign:'center', flexShrink:0 },
  editLabel:    { fontSize:12, fontWeight:600, color:'#9090B8', flex:1 },
  reqBadge:     { fontSize:9, fontWeight:700, color:'#5B5B80', background:'rgba(255,255,255,.06)', padding:'2px 6px', borderRadius:10, flexShrink:0 },
  visBtn:       { padding:'3px 9px', borderRadius:12, border:'none', fontWeight:700, cursor:'pointer', fontSize:10, fontFamily:'inherit', flexShrink:0 },
  visBtnOn:     { background:'rgba(52,211,153,.2)', color:'#34D399' },
  visBtnOff:    { background:'rgba(247,96,92,.15)', color:'#F7605C' },
  // Layout
  overlay:      { position:'fixed', inset:0, background:'rgba(0,0,0,.5)', zIndex:290 },
  main:         { flex:1, display:'flex', flexDirection:'column', overflow:'hidden', minWidth:0 },
  topbar:       { display:'flex', alignItems:'center', padding:'0 24px', height:62, gap:14, flexShrink:0 },
  menuBtn:      { fontSize:20, background:'none', border:'none', cursor:'pointer', color:'rgba(255,255,255,0.6)', padding:'0 4px' },
  pageTitle:    { fontSize:18, fontWeight:800, margin:0, flex:1 },
  topRight:     { marginLeft:'auto', flexShrink:0, display:'flex', alignItems:'center', gap:10 },
  content:      { flex:1, overflowY:'auto', overflowX:'hidden', padding:'20px 24px' },

  // Mer-ark (mobil)
  moreItem:      { display:'flex', alignItems:'center', gap:12, padding:'12px 8px', borderRadius:12, border:'none', background:'transparent', cursor:'pointer', width:'100%', fontFamily:'inherit', transition:'background .12s' },
  moreItemActive:{ background:'rgba(124,111,247,.12)' },
  moreBadge:     { minWidth:18, height:18, borderRadius:9, background:'#F7605C', color:'#fff', fontSize:10, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 4px', lineHeight:1 },

  // Varselklokke
  notifBtn:      { position:'relative', width:38, height:38, borderRadius:12, border:'1px solid rgba(255,255,255,0.12)', background:'rgba(255,255,255,0.08)', backdropFilter:'blur(10px)', cursor:'pointer', fontSize:16, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 },
  notifBadge:    { position:'absolute', top:-4, right:-4, minWidth:17, height:17, borderRadius:9, background:'#F7605C', color:'#fff', fontSize:9, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 4px', fontFamily:'inherit', lineHeight:1 },
  notifOverlay:  { position:'fixed', inset:0, zIndex:999 },
  notifPanel:    { position:'fixed', top:72, right:16, width:330, maxHeight:440, background:'rgba(20,18,40,0.96)', backdropFilter:'blur(24px)', WebkitBackdropFilter:'blur(24px)', borderRadius:18, border:'1px solid rgba(255,255,255,0.1)', boxShadow:'0 12px 48px rgba(0,0,0,.5)', zIndex:1000, display:'flex', flexDirection:'column', overflow:'hidden' },
  notifHeader:   { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 16px 12px', borderBottom:'1px solid rgba(255,255,255,0.08)', flexShrink:0 },
  notifTitle:    { fontSize:14, fontWeight:800, color:'#fff' },
  notifAction:   { background:'none', border:'none', cursor:'pointer', fontSize:11, fontWeight:700, color:'#A78BFA', fontFamily:'inherit', padding:'2px 6px' },
  notifList:     { overflowY:'auto', flex:1 },
  notifEmpty:    { padding:'32px 16px', textAlign:'center', color:'rgba(255,255,255,0.3)', fontSize:13 },
  notifItem:     { display:'flex', alignItems:'flex-start', gap:10, padding:'11px 16px', borderBottom:'1px solid rgba(255,255,255,0.06)', cursor:'pointer', transition:'background .15s' },
  notifItemUnread:{ background:'rgba(124,111,247,0.08)' },
  notifItemIcon: { fontSize:18, flexShrink:0, marginTop:1 },
  notifItemBody: { flex:1, minWidth:0 },
  notifItemTitle:{ fontSize:13, fontWeight:700, color:'rgba(255,255,255,0.9)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  notifItemText: { fontSize:11, color:'rgba(255,255,255,0.45)', marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  notifItemTime: { fontSize:10, color:'rgba(255,255,255,0.3)', marginTop:3 },
  notifDot:      { width:7, height:7, borderRadius:'50%', background:'#7C6FF7', flexShrink:0, marginTop:5 },
  adminBadge:    { marginLeft:'auto', minWidth:18, height:18, borderRadius:9, background:'#F7605C', color:'#fff', fontSize:10, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 5px', lineHeight:1 },
};
